import { FieldExtensionComponentProps, createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import type { FieldValidation } from '@rjsf/utils';
import type { JsonObject } from '@backstage/types';
import { useEffect, useMemo, useRef } from 'react';
import { getGcpProjectIdFromForm } from './utils';
import { ENV_REQUIRED_ERROR } from './constants';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from '@material-ui/core';

const ALL_ENVS = ['production', 'staging', 'qa'] as const;
type Env = (typeof ALL_ENVS)[number];

/**
 * Returns the envs allowed for a given GCP project ID based on its suffix:
 *   -prod / -prd  → production only
 *   -nonprod      → staging + qa
 *   -stg          → staging only
 *   -qa           → qa only
 *   (no suffix)   → all three
 */
export function getAllowedEnvs(gcpId: string | undefined): Env[] {
  if (!gcpId) return [...ALL_ENVS];
  if (/-(prod|prd)$/.test(gcpId)) return ['production'];
  if (/-nonprod$/.test(gcpId)) return ['staging', 'qa'];
  if (/-stg$/.test(gcpId)) return ['staging'];
  if (/-qa$/.test(gcpId)) return ['qa'];
  return [...ALL_ENVS];
}

export const EnvsField = ({
  onChange,
  rawErrors,
  formContext,
  formData,
  idSchema,
  required,
  uiSchema,
  disabled,
  readonly,
}: FieldExtensionComponentProps<string[]>) => {
  const isLocked = disabled || readonly;
  const gcpProjectId = getGcpProjectIdFromForm(formContext?.formData as JsonObject | undefined);
  const allowedEnvs = useMemo(() => getAllowedEnvs(gcpProjectId), [gcpProjectId]);
  const rawSelected = Array.isArray(formData) ? formData : [];
  const selected = rawSelected.filter((v): v is Env =>
    (allowedEnvs as readonly string[]).includes(v),
  );

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    const newAllowed = getAllowedEnvs(gcpProjectId);
    const current = Array.isArray(formData) ? formData : [];
    const filtered = current.filter(v => (newAllowed as readonly string[]).includes(v));
    if (filtered.length < current.length) {
      onChangeRef.current(filtered as Env[], undefined, idSchema.$id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gcpProjectId]);

  const handleToggle = (env: Env) => {
    if (selected.includes(env)) {
      onChange(selected.filter((item: string) => item !== env), undefined, idSchema.$id);
    } else {
      onChange([...selected, env], undefined, idSchema.$id);
    }
  };

  return (
    <FormControl component="fieldset" required={required} error={(rawErrors?.length ?? 0) > 0} margin="normal" fullWidth>
      <FormLabel component="legend">{(uiSchema['ui:title'] as string | undefined) || 'Environments'}</FormLabel>
      <FormGroup row>
        {allowedEnvs.map((env) => (
          <FormControlLabel
            key={env}
            label={env}
            control={
              <Checkbox
                checked={selected.includes(env)}
                disabled={isLocked}
                onChange={() => handleToggle(env)}
              />
            }
          />
        ))}
      </FormGroup>
      {(rawErrors?.length ?? 0) > 0 && (
        <FormHelperText>{rawErrors[0] ?? ENV_REQUIRED_ERROR}</FormHelperText>
      )}
    </FormControl>
  );
};

export const EnvsFieldValidation = (
  values: string[] | undefined,
  validation: FieldValidation,
  { formData }: { formData: JsonObject },
) => {
  const gcpId = getGcpProjectIdFromForm(formData);
  const allowedSet = new Set<string>(getAllowedEnvs(gcpId));
  const validSelections = (values ?? []).filter(v => allowedSet.has(v));

  if (validSelections.length === 0) {
    validation.addError(ENV_REQUIRED_ERROR);
  }
};

export const EnvsFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'EnvsField',
    component: EnvsField,
    validation: EnvsFieldValidation,
  }),
);
